import { shallowRef } from '@vue/reactivity'
import { walkDOM } from './walker.js'
import { registeredDirectives } from '../directives'
import {
    components,
    componentCleanupMap,
    componentScopeByElement,
    registerComponentCleanup,
    makeReactiveScope,
} from './reactive.js'
import { evaluateInScope } from './utils.js'

const SCRIPT_RESERVED_ATTRIBUTES = new Set([ 'arbor', 'arbor-processed' ])

export function initComponents() {

    for (const component of window.__arborComponents) {

        if (componentCleanupMap.has(component.script)) {
            continue
        }

        if (!component.script.isConnected) {
            continue
        }

        const element = component.script.nextElementSibling

        if (!element) {

            console.error('No sibling element found for component.', component)

            continue

        }

        if (componentCleanupMap.has(element)) {
            continue
        }

        const parentScopeHolder = findAncestorScopeHolder(element)
        const props = readScriptProperties(component.script, parentScopeHolder)
        const scope = resolveScope(component.definition, props)

        if (scope) {
            registerComponentCleanup(element, initComponent(element, scope, props))
        }

    }

    window.__arborComponents.length = 0

}

export function reviveContentIn(root, scope) {

    if (!root) {
        return []
    }

    return parseDirectives(root, scope)

}

export function initComponent(element, scope, props = {}) {

    if (element._qvInitialized) {
        return element._qvCleanupCallbacks || []
    }

    element._qvInitialized = true

    const baseComponent = {
        get $store() {
            return Arbor.store('qv')
        },
        get $state() {
            return Arbor.store('state')
        },
        get $el() {
            return element
        },
        get $router() {
            return Arbor.router()
        },
        get $props() {
            return props
        },
        $watch(target, callback, options) {

            const watchCleanup = typeof target === 'string'
                ? Arbor.watch(() => evaluateInScope(target, this), callback, options)
                : Arbor.watch(target, callback, options)

            if (!componentCleanupMap.has(element)) {
                componentCleanupMap.set(element, [])
            }

            componentCleanupMap.get(element).push(watchCleanup)

            return watchCleanup

        },
        $refs: {},
        $nextTick(callable) {
            return Arbor.nextTick(callable)
        },
    }

    const component = makeReactiveScope(baseComponent, scope)

    /*
     * Register the scope BEFORE parseDirectives runs. qv-for and friends will
     * fire from inside parseDirectives, and any nested <script arbor :prop>
     * tags initialized during that sweep must be able to walk up the DOM and
     * find this component as an ancestor scope.
     */
    componentScopeByElement.set(element, component)

    if (!element._qvScopeHolder) {
        element._qvScopeHolder = shallowRef(component)
    } else {
        element._qvScopeHolder.value = component
    }

    const cleanCallbacks = parseDirectives(element, component)

    if (typeof component.init === 'function') {
        component.init()
    }

    components.push(component)

    return cleanCallbacks

}

function findAncestorScopeHolder(element) {

    let cursor = element.parentElement

    while (cursor) {

        if (cursor._qvScopeHolder) {
            return cursor._qvScopeHolder
        }

        if (componentScopeByElement.has(cursor)) {

            const holder = shallowRef(componentScopeByElement.get(cursor))

            cursor._qvScopeHolder = holder

            return holder

        }

        cursor = cursor.parentElement

    }

    return null

}

function readScriptProperties(script, parentScopeHolder) {

    const props = {}

    for (const attribute of Array.from(script.attributes)) {

        if (SCRIPT_RESERVED_ATTRIBUTES.has(attribute.name)) {
            continue
        }

        if (attribute.name.startsWith(':')) {

            const propertyName = attribute.name.slice(1)
            const expression = attribute.value

            /*
             * Plain getter: when a consumer effect (qv-text, qv-bind, ...)
             * reads $props.foo it calls evaluateInScope on the parent's
             * reactive scope inside that effect, so dependency tracking is
             * established directly on the parent. No intermediate ref or
             * effect runner to dispose of.
             */
            Object.defineProperty(props, propertyName, {
                get() {

                    const scope = parentScopeHolder?.value

                    return scope ? evaluateInScope(expression, scope) : null

                },
                enumerable: true,
                configurable: true,
            })

            continue

        }

        props[ attribute.name ] = attribute.value

    }

    return props

}

export function parseDirectives(element, component) {

    const cleanCallbacks = []
    const nodes = []

    walkDOM(element, node => {
        nodes.push(node)
    })

    for (const node of nodes) {

        for (const directive of registeredDirectives) {

            const cleanCallback = directive.handle(node, component)

            if (cleanCallback) {
                cleanCallbacks.push(cleanCallback)
            }

        }

    }

    // Store cleanup callbacks on the element for use when removing
    element._qvCleanupCallbacks = cleanCallbacks

    return cleanCallbacks

}

function resolveScope(definition, props) {

    let scope = evaluateInScope(definition)

    if (typeof scope === 'function') {
        scope = scope(props)
    }

    return scope

}
